import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ADMIN_ORDERS_KEY } from '../../../api/admin-orders';
import {
  ADMIN_REFUNDS_KEY,
  postIssueRefund,
  type IssueRefundBody,
} from '../../../api/admin-refunds';
import { publishToast } from '../../../components/feedback';

/**
 * Encapsulates the order-initiated issue-refund orchestration (AUT-129 /
 * ADMIN-17). Its ONLY caller is the order detail's `IssueRefundDialog`
 * (architect S1 — NOT a premature shared abstraction).
 *
 * A single unconditional POST: AUT-138 removed per-action TOTP step-up, so
 * the refund endpoint can never return `401 totp_step_up_required` again. It
 * returns 200 on success, or `409 refund_exceeds_captured` from the
 * independent refund-cap guard. The action is role + permission + audit
 * gated server-side.
 *
 * On success it invalidates BOTH the `['admin','refunds']` history-list
 * cache AND the `['admin','orders']` cache so the originating order
 * detail's PaymentTab re-reads `refundedTotal` (architect guardrail #3).
 */

/** Map a server error to its i18n key, else the generic one. */
const issueRefundErrorKey = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    const code = (err.response?.data as { code?: string } | undefined)?.code;
    if (code === 'payment_not_refundable')
      return 'orders.issueRefundDialog.error.payment_not_refundable';
    if (code === 'refund_amount_exceeds')
      return 'orders.issueRefundDialog.error.refund_amount_exceeds';
  }
  return 'orders.issueRefundDialog.error.generic';
};

export interface IssueRefundVariables {
  paymentId: string;
  body: IssueRefundBody;
}

export interface UseIssueRefundResult {
  /** True while the refund is in flight. */
  busy: boolean;
  /** Run a refund; resolves true on success, false on error. */
  run: (variables: IssueRefundVariables) => Promise<boolean>;
}

export const useIssueRefund = (): UseIssueRefundResult => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ADMIN_REFUNDS_KEY }),
      queryClient.invalidateQueries({ queryKey: ADMIN_ORDERS_KEY }),
    ]);
  };

  const run = async ({ paymentId, body }: IssueRefundVariables): Promise<boolean> => {
    setBusy(true);
    try {
      await postIssueRefund(paymentId, body);
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t('orders.issueRefundDialog.successToast'),
      });
      return true;
    } catch (err: unknown) {
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t(issueRefundErrorKey(err)),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
